import type { VisitorApiData } from "../../../../../utils/visitorApi";

export type { VisitorApiData };

export interface IRevenueTrendPoint {
  label: string;
  total: number;
}

export interface ISignupsTrendPoint {
  label: string;
  count: number;
}

export interface IConversionTotals {
  visits: number;
  signups: number;
  paid: number;
  signupRate: number; // 0..1
  payRate: number; // 0..1
  firstPayRate: number;
}
export type Granularity = "daily" | "weekly" | "monthly";

export interface IConversion {
  from: string;
  to: string;
  granularity: Granularity;
  totals: IConversionTotals;
  points: Array<{
    label: string;
    visits: number;
    signups: number;
    paid: number;
  }>;
}

export interface IRetention {
  baseCreators90d: number;
  baseUsers90d?: number;

  rates: { r30: number; r60: number; r90: number }; // у відсотках
  counts: { n30: number; n60: number; n90: number };
}
export type BreakdownSummary = {
  tips: number;
  subscriptions: number;
  customVideo: number; // <- з бекенду
  postPurchase: number; // <- з бекенду
};
export interface IAdminSummary {
  period: { from: string; to: string };
  gross: number;
  breakdown: BreakdownSummary;
  activeCreators: number;
}
export interface IAdminRevenueMonth {
  month: string; // 'YYYY-MM'
  total: number;
  breakdown: {
    tip?: number;
    subscription?: number;
    "custom-video"?: number;
    "post-purchase"?: number;
  };
}
export interface ITopCreator {
  creatorId: string;
  username: string;
  name: string;
  profileImg?: string;
  total: number;
  breakdown?: Record<string, number>;
}

export interface ITopSpender {
  userId: string;
  username: string;
  name: string;
  profileImg?: string;
  total: number;
  breakdown?: Record<string, number>;
}
export interface IAdminOverview {
  period: { from: string; to: string };
  kpis: {
    totalOnlineCreators: number;
    totalActiveCreators: number;
    totalRegisteredUsers: number;
    mau30d: number;
    revenueThisMonth: number;
    payoutsThisMonth: number;
    pendingPayouts: { amount: number; count: number };
    newSignups: { today: number; week: number };
  };
}
export interface IMonetization {
  period: { from: string; to: string };
  payouts: {
    processed: { amount: number; count: number };
    pending: { amount: number; count: number };
    failed: { amount: number; count: number };
  };
  types: {
    tip: { amount: number; count: number };
    subscription: { amount: number; count: number };
    "custom-video": { amount: number; count: number };
    "post-purchase": { amount: number; count: number };
  };
  averageEarningsPerCreator: number;
  tools: Array<{
    tool: string;
    label: string;
    amount: number;
    txnCount: number;
    creators: number;
  }>;
}

export interface ICreatorActivity {
  period: { from: string; to: string };
  activeCreatorsToday: number;
  uploadVolume: { photos: number; videos: number };
  flagged: {
    counts: { pending: number; flagged: number; rejected: number };
    recent: Array<{
      id: string;
      type: string; // photo | video | post
      status: string; // pending | flagged | rejected
      reason?: string;
      creator?: { id?: string; username?: string; name?: string };
      createdAt?: string;
    }>;
  };
}
export interface ITransactions {
  period: { from24h: string; from7d: string; to: string };
  volume24h: Array<{
    hour: string;
    total: number;
    success: number;
    failed: number;
    bucket: string;
  }>;
  volume7d: Array<{
    day: string;
    total: number;
    success: number;
    failed: number;
  }>;
  failed: {
    byCode: Array<{ code: string; count: number; amount: number }>;
    recent: Array<{
      at: string;
      amount: number;
      code?: string;
      reason?: string;
      user?: { id?: string; username?: string; name?: string };
    }>;
  };
  refunds: {
    totals: {
      pending: { count: number; amount: number };
      approved: { count: number; amount: number };
    };
    recent: Array<{
      at: string;
      status: string;
      amount: number;
      reason?: string;
      user?: { id?: string; username?: string; name?: string };
    }>;
  };
}
// має відповідати JSON з бекенда
export interface ICompliance {
  kpis: {
    creators18Plus: { percent: number; verified: number; total: number };
    flaggedPending: {
      total: number;
      photos: number;
      videos: number;
      other: number;
    };
    dmcaRecentCount: number;
    uptimePercent: number;
  };
  dmca: {
    recent: Array<{
      at: string;
      type: string;
      contentId: string;
      action: string;
      reporter?: string;
    }>;
  };
  custodian2257: {
    lastAuditAt: string | null;
    missingDocsCount: number;
    recent: Array<{
      at: string;
      creatorId: string;
      action: string;
      note?: string;
    }>;
  };
  downtime: {
    days: number;
    percent: number;
    events: Array<{ start: string; end?: string | null; reason?: string }>;
  };
}

type BarPoint = { label: string; value: number };

export interface SimpleBarChartProps {
  data: BarPoint[];
  height?: number;
  color?: string;
  valueFormat?: (v: number) => string; // формат значення в тултіпі/на осі
  tooltipLabelFormat?: (label: string) => string; // формат підпису в тултіпі
}

export type PayoutState = { amount: number; tx: number };
export type PayoutsOverview = {
  period: { from: string; to: string };
  totals: {
    processed: PayoutState;
    pending: PayoutState;
    failed: PayoutState;
    all: PayoutState;
  };
};

export type AvgEarnings = {
  period: { from: string; to: string };
  gross: number;
  activeCreators: number;
  averages: {
    perActiveCreator: number;
    perCreatorOverall: number | null;
  };
};

export type RevenueTrendPoint = { label: string; total: number };
export type RevenueTrendResponse = {
  from: string;
  to: string;
  granularity: Granularity;
  points: RevenueTrendPoint[];
};
export type SignupsTrendPoint = { label: string; count: number };
export type TimePreset = "day" | "week" | "month";
export type UploadPoint = {
  label: string;
  photos: number;
  videos: number;
  total: number;
};
// активні зараз (sessions-now)
export type ActiveSessionRow = {
  userId: string;
  username?: string;
  email?: string;
  ip: string;
  ua: string;
  lastSeenAt: string; // ISO
  profileImg?: string;
  /** Знімок Visitor API або аналог, якщо бекенд зберігає його для сесії */
  visitor?: VisitorApiData;
};

// visitors (IP+UA агрегат)
export type VisitorIpRow = {
  Time: string; // ISO
  IP: string;
  UA: string;
  Hits: number;
  UniqueUsers: number;
};

// історія IP користувача
export type UserIpRow = {
  Time: string; // ISO
  IP: string;
  UA: string;
  Hits: number;
};
export type UsersLastIpRow = {
  userId: string;
  username?: string;
  email?: string;
  profileImg?: string;
  lastAt: string;
  ip?: string;
  ua?: string;
  hits: number;
  visitor?: VisitorApiData;
};
export type ActiveTodayAPI = {
  count?: number;
  totalActiveUsersToday?: number;
  totalActiveCreatorsToday?: number;
  breakdown?: { sessions?: number; uploaders?: number; payers?: number };
};
export const normalizeCompliance = (d: any): ICompliance => {
  const kpis = d?.kpis ?? {
    creators18Plus: d?.creators18Plus ??
      d?.kpi?.creators18Plus ?? { verified: 0, total: 0, percent: 0 },
    flaggedPending: d?.flaggedPending ??
      d?.kpi?.flaggedPending ?? { total: 0, photos: 0, videos: 0, other: 0 },
    dmcaRecentCount: d?.dmcaRecentCount ?? d?.kpi?.dmcaRecentCount ?? 0,
    uptimePercent:
      d?.uptimePercent ?? d?.kpi?.uptimePercent ?? d?.downtime?.percent ?? 0,
  };
  return {
    kpis,
    dmca: d?.dmca ?? { recent: [] },
    custodian2257: d?.custodian2257 ?? {
      lastAuditAt: null,
      missingDocsCount: 0,
      recent: [],
    },
    downtime: d?.downtime ?? { days: 30, percent: 0, events: [] },
  };
};

export const contexts = [
  { label: "All", value: "" },
  { label: "Tips", value: "tip" },
  { label: "Subscriptions", value: "subscription" },
  { label: "Custom Video", value: "custom-video" },
  { label: "Post Purchases", value: "post-purchase" },
];
